import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import type { CXRootStore } from '../../stores/cx-root.store';
import {
    IInitiatePaymentProps,
    IInitPaymentActionResponse,
    IPaidPaymentInfo,
    IPayActionResponse,
    IPayProps,
    IPayWithPaymentMethodProps,
    PaymentMethodType,
    ISetPaymentAsChargedProps
} from '../../../../types/pay.types';
import { getForterToken } from '../../../../utils/client-experience.utils';
import {
    AutoPayStatus,
    IBankAccountResponse,
    IFlowUpdatedResponse,
    IPaymentMethodResponse,
    IPaymentVariableValue,
    IRecurringInvoiceResponse,
    IScheduleContainerVariableValue,
    IVariableResponse
} from '../../../../types/server.types';
import { LogEvents } from '../../services/logger/log-events';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';
import { getTransComponent } from '~/utils/text.utils';
import { IPostPaymentActions } from '~/components/ClientExperience/services/pay.service';
import { TTrackFunction } from '~/common/useAnalytics';
import { Token } from '@stripe/stripe-js';

export class PayController {
    rootStore: CXRootStore;

    constructor(rootStore: CXRootStore) {
        this.rootStore = rootStore;
    }

    async validatePayment({
        paymentId,
        paymentMethodId,
        gratuity
    }: {
        paymentId: string;
        paymentMethodId: string;
        gratuity: number;
    }) {
        const {
            payService,
            flowViewUiStore: { selectedFlow },
            flowService
        } = this.rootStore;
        const isPaymentValid = payService.validatePayment({
            paymentId,
            paymentMethodId,
            flowId: selectedFlow._id,
            forterToken: getForterToken(),
            gratuity
        });

        const isFlowValid = flowService.validateSubmit({
            flowId: selectedFlow._id,
            revisionId: selectedFlow.revision_id
        });

        try {
            // isFlowValid will raise in case there is an issue
            // so we just need to check Forter pre charge validation result
            const result = await Promise.all([isPaymentValid, isFlowValid]);
            if (!result[0]) {
                throw new Error('invalid payment request');
            }
            return null;
        } catch (error) {
            return error;
        }
    }

    // TODO: remove eslint-disable
    // eslint-disable-next-line consistent-return
    async payWithPaymentMethod({
        paymentMethodId,
        autoPay,
        gratuity,
        paymentId,
        postPaymentActions
    }: IPayWithPaymentMethodProps): Promise<IFlowUpdatedResponse | any> {
        const validationError = await this.validatePayment({
            paymentId,
            paymentMethodId,
            gratuity
        });

        if (!validationError) {
            const {
                fullstoryService,
                payService,
                flowViewUiStore: { selectedFlow }
            } = this.rootStore;
            const paymentResult = await payService.pay({
                paymentId,
                paymentMethodId,
                flowId: selectedFlow._id,
                revisionId: selectedFlow.revision_id,
                forterToken: getForterToken(),
                autoPay,
                gratuity,
                postPaymentActions
            });

            fullstoryService.debug(LogEvents.PaymentPaid(paymentId));

            return paymentResult;
        }

        // in case payment was not valid. This error will be catched
        // in the pay method
        throw {
            data: {
                error_message:
                    'Your payment method was declined. Please try again, or contact your bank for assistance. Any further questions? Please contact concierge@honeybook.com'
            }
        };
    }

    async prePaymentActions(track: TTrackFunction) {
        const { shouldAbortPayment, scheduling } =
            await this.getPostPaymentSchedulingActions(track);

        return {
            shouldAbortPayment,
            postPaymentActions: { scheduling }
        };
    }

    async getPostPaymentSchedulingActions(track: TTrackFunction): Promise<{
        shouldAbortPayment: boolean;
        scheduling?: IPostPaymentActions['scheduling'];
    }> {
        const {
            scheduleCtrl,
            flowViewUiStore,
            flowViewUiStore: {
                selectedFlow: { scheduleContainerBlock }
            }
        } = this.rootStore;

        if (!scheduleContainerBlock?.isSlotSelected) {
            return { shouldAbortPayment: false };
        }

        const { memberIds, error } = await scheduleCtrl.validateSlot(
            scheduleContainerBlock,
            scheduleContainerBlock.selectedDate,
            scheduleContainerBlock.clientTimezone
        );

        if (error) {
            track(AnalyticsEvents.SlotNotAvailable, {
                action: 'load',
                source: 'payment page',
                type: error
            });
            flowViewUiStore.toggleSlotUnavailableDialog(error);
            scheduleContainerBlock.setTempVariableValue(null);
            return { shouldAbortPayment: true };
        }

        return {
            shouldAbortPayment: false,
            scheduling: {
                page_id: scheduleContainerBlock.page_id,
                variable_id: scheduleContainerBlock.connected_variable_id,
                data: {
                    client_timezone: scheduleContainerBlock.clientTimezone,
                    selected_date: scheduleContainerBlock.selectedDate,
                    user_ids: memberIds
                }
            }
        };
    }

    async initiatePayment({
        paymentId,
        paymentMethod,
        paymentMethodId,
        track,
        logger,
        gratuity,
        activateAutoPay
    }: IInitiatePaymentProps): Promise<IInitPaymentActionResponse> {
        const {
            payService,
            flowStore,
            flowViewUiStore: { selectedFlow, setIsPaymentInProgress }
        } = this.rootStore;
        const { toastStore } = this.rootStore.globalContext;
        setIsPaymentInProgress(true);

        toastStore.add({
            type: ToastType.Info,
            text: `Processing your payment`,
            autoDismissMs: 3000
        });

        try {
            let pmId = paymentMethodId;
            if (!paymentMethodId && paymentMethod) {
                // Create payment method
                const pm = await this.createPaymentMethod({
                    stripePaymentMethod: paymentMethod,
                    paymentMethodType: PaymentMethodType.CreditCard
                });

                pmId = pm._id;
            }

            const validationError = await this.validatePayment({
                paymentId,
                paymentMethodId: pmId,
                gratuity
            });

            if (validationError) {
                throw {
                    data: {
                        error_message:
                            'Your payment method was declined. Please try again, or contact your bank for assistance. Any further questions? Please contact concierge@honeybook.com'
                    }
                };
            }

            const { shouldAbortPayment, postPaymentActions } =
                await this.prePaymentActions(track);

            if (shouldAbortPayment) {
                return;
            }

            logger.trackUserAction({
                name: 'InitiatePayment',
                context: {
                    paymentId,
                    paymentMethodId: pmId,
                    paymentMethod: paymentMethod?.id
                }
            });

            const { revision_id: revisionId, _id: flowId } = selectedFlow;

            const resp = await payService.initiatePayment({
                paymentId,
                flowId,
                revisionId,
                paymentMethodId: pmId,
                gratuity,
                postPaymentActions,
                activateAutoPay,
                forterToken: getForterToken()
            });

            if ((resp as any)?.data?.error_type === 'HBFlowIsExpiredError') {
                toastStore.add({
                    type: ToastType.Error,
                    text: `${
                        selectedFlow.title
                    } has expired and cannot be payed for.${
                        selectedFlow?.vendor?.company?.company_name &&
                        ` Please contact ${selectedFlow.vendor.company.company_name} for more details.`
                    }`,
                    autoDismissMs: 12000
                });
                // eslint-disable-next-line consistent-return
                return;
            }

            flowStore.syncSelectedFlowWithServerResponse(
                resp.pages,
                resp.blocks,
                resp.variables
            );

            return {
                ...resp,
                payment_method: {
                    ...resp.payment_method,
                    _id: pmId
                }
            };
        } catch (error) {
            logger.trackUserAction({
                name: 'InitiatePaymentFailed',
                context: {
                    error: error?.data?.error_message,
                    type: error?.data?.error_type
                }
            });
            toastStore.add({
                title: 'Something went wrong',
                text: error?.data?.error_message,
                type: ToastType.Error,
                autoDismissMs: 0
            });

            return {
                client_secret: '',
                requires_confirmation: false,
                payment_method: null,
                statement_description: '',
                charge_id: '',
                amount: 0
            };
        } finally {
            setIsPaymentInProgress(false);
        }
    }

    async setPaymentAsCharged({
        chargeId,
        paymentId,
        track,
        paymentMethodId,
        logger,
        activateAutoPay
    }: ISetPaymentAsChargedProps & {
        track: TTrackFunction;
    }) {
        const {
            payService,
            flowViewUiStore: { selectedFlowId: flowId },
            flowStore,
            globalContext: { toastStore }
        } = this.rootStore;
        try {
            const { postPaymentActions } = await this.prePaymentActions(track);

            const { pages, blocks, variables } =
                await payService.setPaymentAsCharged({
                    chargeId,
                    paymentId,
                    flowId,
                    paymentMethodId,
                    activateAutoPay,
                    postPaymentActions
                });

            flowStore.syncSelectedFlowWithServerResponse(
                pages,
                blocks,
                variables
            );

            await this.paymentSuccessSideEffects({
                variables,
                postPaymentActions,
                track
            });
        } catch (error) {
            logger.trackUserAction({
                name: 'SetPaymentAsChargedFailed',
                context: {
                    error: error?.data?.error_message,
                    type: error?.data?.error_type
                }
            });
            toastStore.add({
                title: 'Something went wrong',
                text: '',
                type: ToastType.Error,
                autoDismissMs: 0
            });
        }
    }

    async pay({
        paymentId,
        paymentMethodId,
        autoPay,
        gratuity,
        shouldExecutePayment = true,
        track,
        stripePaymentMethod,
        logger,
        shouldAddPaymentMethod = true
    }: IPayProps): Promise<IPayActionResponse> {
        const { flowViewUiStore, toastStore, flowStore } = this.rootStore;

        const { selectedFlow } = flowViewUiStore;

        let paymentMethod: IPaymentMethodResponse;
        let paidPaymentInfo: IPaidPaymentInfo;
        try {
            flowViewUiStore.setIsPaymentInProgress(true);

            if (shouldExecutePayment) {
                toastStore.add({
                    type: ToastType.Info,
                    text: `Processing your payment`,
                    autoDismissMs: 3000
                });
            }

            if (!paymentMethodId) {
                // Create payment method
                track(AnalyticsEvents.CXAddNewCreditCard);
                paymentMethod = await this.createPaymentMethod({
                    stripePaymentMethod,
                    paymentMethodType: PaymentMethodType.CreditCard,
                    shouldAddPaymentMethod
                });

                // In the case where payment method was added via modal
                // we don't want to execute the payment immediately
                if (!shouldExecutePayment) {
                    flowViewUiStore.setIsPaymentInProgress(false);

                    return this.getPayResponse(null, paymentMethod);
                }
            }

            const { shouldAbortPayment, postPaymentActions } =
                await this.prePaymentActions(track);

            if (shouldAbortPayment) {
                return;
            }

            const response = await this.payWithPaymentMethod({
                paymentMethodId: paymentMethod
                    ? paymentMethod._id
                    : paymentMethodId,
                autoPay,
                gratuity,
                paymentId,
                postPaymentActions
            });

            if (response?.data?.error_type === 'HBFlowIsExpiredError') {
                toastStore.add({
                    type: ToastType.Error,
                    text: `${
                        selectedFlow.title
                    } has expired and cannot be payed for.${
                        selectedFlow?.vendor?.company?.company_name &&
                        ` Please contact ${selectedFlow.vendor.company.company_name} for more details.`
                    }`,
                    autoDismissMs: 12000
                });
                // eslint-disable-next-line consistent-return
                return;
            }
            paidPaymentInfo = this.getPaidPaymentInfo(response);

            flowStore.syncSelectedFlowWithServerResponse(
                response.pages,
                response.blocks,
                response.variables
            );

            this.paymentSuccessSideEffects({
                ...response,
                postPaymentActions,
                track
            });

            return this.getPayResponse(paidPaymentInfo, paymentMethod);
        } catch (error) {
            logger.trackUserAction({
                name: 'PaymentFailed',
                context: {
                    error: error?.data?.error_message,
                    type: error?.data?.error_type
                }
            });
            toastStore.add({
                title: 'Something went wrong',
                text: error?.data?.error_message,
                type: ToastType.Error,
                autoDismissMs: 0
            });
            this.rootStore.analyticsService.track(
                AnalyticsEvents.CXPaymentToastLoaded,
                {
                    type: 'stripe error',
                    sub_type: error?.data?.error_type
                }
            );
            return this.getPayResponse(paidPaymentInfo, paymentMethod);
        } finally {
            flowViewUiStore.setIsPaymentInProgress(false);
        }
    }

    async paymentSuccessSideEffects({
        variables,
        postPaymentActions,
        track
    }: {
        variables: IVariableResponse[];
        postPaymentActions: IPostPaymentActions;
        track: TTrackFunction;
    }) {
        if (postPaymentActions.scheduling) {
            this.verifyMeetingScheduledAndAddToast(variables, track);
        }

        sessionStorage.removeItem('hb_slot_selected_flow_id');
    }

    verifyMeetingScheduledAndAddToast(
        variables: IVariableResponse<IScheduleContainerVariableValue>[],
        track: TTrackFunction
    ) {
        const {
            toastStore,
            flowCtrl,
            flowViewUiStore: {
                selectedFlow: { scheduleContainerBlock }
            }
        } = this.rootStore;

        const isSessionScheduledSuccessfully =
            variables.find(
                v => v.connected_block_id === scheduleContainerBlock.block_id
            )?.value.status === 'scheduled';

        if (isSessionScheduledSuccessfully) {
            track(AnalyticsEvents.MeetingWasScheduled, {
                source: 'upon payment'
            });
            scheduleContainerBlock.setTempVariableValue(null);
        } else {
            const prefix =
                'client-experience:schedule_block.errors.schedule_after_pay';
            toastStore.add({
                type: ToastType.Error,
                title: getTransComponent(`${prefix}.title`),
                text: getTransComponent(`${prefix}.text`),
                isDismissible: false,
                autoDismissMs: 0,
                actionLink: {
                    color: 'blue20',
                    weight: 'bold',
                    text: getTransComponent(`${prefix}.action`),
                    onClick: () => flowCtrl.goToSchedulePage()
                }
            });
        }
    }

    async createPaymentMethod({
        paymentMethodType,
        stripeToken,
        stripePaymentMethod,
        shouldAddPaymentMethod
    }: Pick<IPayProps, 'stripePaymentMethod' | 'shouldAddPaymentMethod'> & {
        paymentMethodType: PaymentMethodType;
        stripeToken?: Token;
    }) {
        const {
            payService,
            userStore: { currUser, addPaymentMethod },
            flowViewUiStore: { selectedFlow }
        } = this.rootStore;

        const paymentMethod = await payService.createPaymentMethod({
            userId: currUser._id,
            vendorId: selectedFlow.vendor._id,
            stripeToken,
            paymentMethodType,
            stripePaymentMethod,
            cardRole: null,
            flowId: selectedFlow._id
        });

        if (shouldAddPaymentMethod) {
            addPaymentMethod(paymentMethod);
        }
        return paymentMethod;
    }

    getPaidPaymentInfo(
        response: IFlowUpdatedResponse | IRecurringInvoiceResponse
    ): IPaidPaymentInfo {
        let variableValue;

        const paymentBlock = (response as IFlowUpdatedResponse).blocks.find(
            blk => blk.type === 'payment_block'
        );
        variableValue = (response as IFlowUpdatedResponse).variables.find(
            variable =>
                variable.variable_id === paymentBlock?.connected_variable_id
        )?.value as IPaymentVariableValue;

        return {
            charge_id: variableValue.charge_id,
            amount: variableValue.amount,
            gratuity: variableValue.tip_paid,
            payment_id: variableValue.payment_id,
            charge_statement_descriptor:
                variableValue.charge_statement_descriptor,
            payer_email: variableValue.payment_payer_email,
            payer_name: variableValue.payment_payer_name,
            payment_number: variableValue.payment_index,
            charge_date: variableValue.charge_date
        };
    }

    async deletePaymentMethod(id: string) {
        const {
            payService,
            userStore,
            toastStore,
            flowViewUiStore: { selectedFlow }
        } = this.rootStore;

        try {
            await payService.deletePaymentMethod({
                id,
                flowId: selectedFlow._id,
                userId: userStore.currUser._id
            });

            // Response includes user_id and payment methods
            userStore.deletePaymentMethod(id);
        } catch (e) {
            toastStore.add({
                title: 'Something went wrong',
                text: e.data.error_message,
                type: ToastType.Error
            });
        }
    }

    async validateExistingPaymentMethod(
        flowId: string,
        paymentMethodId: string,
        month: string,
        year: string
    ) {
        return this.rootStore.payService.validatePaymentMethodExpirationDate({
            flowId,
            paymentMethodId,
            month,
            year
        });
    }

    setupBankAccount({
        institutionId,
        token
    }: {
        institutionId: string;
        token: string;
    }) {
        const { payService, userStore, flowViewUiStore } = this.rootStore;

        const { currUser } = userStore;

        flowViewUiStore.setIsPaymentInProgress(true);

        return payService.setupBankAccount({
            userId: currUser._id,
            institutionId,
            token
        });
    }

    async createChargeableBankAccount(account: IBankAccountResponse) {
        const {
            userStore: { addPaymentMethod, currUser },
            payService,
            flowViewUiStore
        } = this.rootStore;

        flowViewUiStore.setIsPaymentInProgress(true);
        const paymentMethodsResponse =
            await payService.createChargeableBankAccount({
                account,
                account_key: 'id',
                userId: currUser._id,
                bankRole: null,
                flowId: flowViewUiStore.selectedFlow._id,
                institutionId: account.institution_id,
                institutionName: account.institution_name
            });
        addPaymentMethod(paymentMethodsResponse);

        flowViewUiStore.setIsPaymentInProgress(false);

        return paymentMethodsResponse;
    }

    async setupUserPaymentMethods() {
        const {
            userStore: {
                currUser,
                setFetchedPaymentMethods,
                setFlowPaymentMethods
            },
            payService,
            flowViewUiStore: { selectedFlow, setIsFetchingUserPaymentMethods },
            toastStore
        } = this.rootStore;
        setIsFetchingUserPaymentMethods(true);

        try {
            const paymentMethodsResponse =
                await payService.getUserPaymentMethodsForFlow({
                    userId: currUser._id,
                    flowId: selectedFlow?._id
                });

            setFlowPaymentMethods(paymentMethodsResponse);
        } catch (e) {
            toastStore.add({
                title: 'Something went wrong',
                text: e?.data?.error_message,
                type: ToastType.Error
            });
        }

        setIsFetchingUserPaymentMethods(false);
        setFetchedPaymentMethods(true);
    }

    getPayResponse(
        paidPaymentInfo: IPaidPaymentInfo,
        paymentMethod: IPaymentMethodResponse
    ) {
        return {
            paidPaymentInfo,
            createdPaymentMethod: paymentMethod ? paymentMethod._id : null,
            createdPaymentMethodResp: paymentMethod
        };
    }

    async updateAutoPayStatus(status: AutoPayStatus) {
        const {
            flowViewUiStore: { selectedFlow },
            payService,
            toastStore,
            flowStore
        } = this.rootStore;

        try {
            const response = await payService.updateFlowAutoPay({
                flowId: selectedFlow._id,
                status
            });

            flowStore.syncSelectedFlowWithServerResponse(
                response.pages,
                response.blocks,
                response.variables
            );
        } catch (e) {
            toastStore.add({
                title: 'Something went wrong',
                text: e?.data?.error_message,
                type: ToastType.Error
            });
        }
    }
}
